import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, TrendingUp, Package, DollarSign,
  Minus, Trash2, LayoutGrid, List, ChevronDown,
  Tag, ArrowLeftRight, SlidersHorizontal, ShoppingCart, BookOpen, Star, Bell, BellOff, Handshake,
  Layers, ShieldCheck, AlertTriangle, Loader2, ChevronRight, ShoppingBag,
} from 'lucide-react';
import {
  useCollection,
  useCollectionSetCompletion,
  useCollectionFilters,
  useUpdateCollectionItem,
  useRemoveFromCollection,
  useAddToCollection,
  useFeatureCollectionItem,
  useFeaturedCollection,
  useSetWatches,
  useToggleSetWatch,
  useDecks,
  useDeck,
  useCreateDeck,
  useDeleteDeck,
  useDeckTemplates,
  useCreateDeckFromTemplate,
} from '@/hooks/useApi';
import { deckService } from '@/lib/api';
import { Deck, DeckCard, DeckGame } from '@/types';
import { formatPrice } from '@/utils';
import SetCompletionBar from '@/components/SetCompletionBar';
import AddToCollectionModal from '@/components/AddToCollectionModal';
import AddBulkMerchModal from '@/components/AddBulkMerchModal';
import QuickListingModal from '@/components/QuickListingModal';
import FindListingsModal from '@/components/FindListingsModal';
import CardImage from '@/components/CardImage';
import toast from 'react-hot-toast';

const SORT_OPTIONS = [
  { value: 'recently_added', label: 'Recently Added' },
  { value: 'name_asc',       label: 'Name: A–Z' },
  { value: 'name_desc',      label: 'Name: Z–A' },
  { value: 'value_desc',     label: 'Highest Value' },
  { value: 'value_asc',      label: 'Lowest Value' },
  { value: 'quantity_desc',  label: 'Most Copies' },
  { value: 'rarity',         label: 'Rarity' },
];

const CONDITIONS = ['Mint', 'Near Mint', 'Excellent', 'Good', 'Played', 'Poor'];

type ViewMode = 'grid' | 'list' | 'sets' | 'albums' | 'offers' | 'decks' | 'bulk_merch';
type StatusFilter = 'all' | 'for_sale' | 'not_listed' | 'for_trade';

const GAME_EMOJI: Record<string, string> = {
  'Pokemon':               '⚡',
  'Magic: The Gathering':  '🔮',
  'Yu-Gi-Oh!':             '⭐',
  'One Piece':             '☠️',
  'Dragon Ball':           '🐉',
  'Dragon Ball Super':     '🐉',
  'Digimon':               '🦕',
  'Baseball':              '⚾',
  'Basketball':            '🏀',
  'Football':              '🏈',
  'Soccer':                '⚽',
  'Hockey':                '🏒',
};

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'all',        label: 'All' },
  { value: 'for_sale',   label: 'For Sale' },
  { value: 'not_listed', label: 'Not Listed' },
  { value: 'for_trade',  label: 'Negotiable' },
];

const DECK_GAMES: DeckGame[] = ['Pokemon', 'Magic', 'YuGiOh'];
const DECK_GAME_EMOJI: Record<DeckGame, string> = { Pokemon: '⚡', Magic: '🔮', YuGiOh: '🃏' };
const DECK_GAME_COLOR: Record<DeckGame, string> = {
  Pokemon: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200',
  Magic:   'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200',
  YuGiOh:  'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
};

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <button
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-zinc-800 text-sm disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800"
      >
        ← Prev
      </button>
      <span className="text-sm text-gray-600 dark:text-zinc-400">Page {page} of {totalPages}</span>
      <button
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-zinc-800 text-sm disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800"
      >
        Next →
      </button>
    </div>
  );
}

function QuantityControl({
  item,
  onUpdate,
  onRemove,
}: {
  item: any;
  onUpdate: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => {
          if (item.quantity <= 1) {
            if (window.confirm(`Remove ${item.card?.name} from your collection?`)) {
              onRemove(item.id);
            }
          } else {
            onUpdate(item.id, item.quantity - 1);
          }
        }}
        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 hover:text-gray-800 dark:text-zinc-200 transition-colors"
        title="Decrease"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-7 text-center text-sm font-bold text-gray-800 dark:text-zinc-200">{item.quantity}</span>
      <button
        onClick={() => onUpdate(item.id, item.quantity + 1)}
        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 hover:text-gray-800 dark:text-zinc-200 transition-colors"
        title="Increase"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const PAGE_SIZE = 30;

// ── Deck Row (Sets-like expandable view) ─────────────────────────────────────

function DeckExpandedContent({ deckId }: { deckId: string }) {
  const { data: deck, isLoading } = useDeck(deckId);
  const cards: DeckCard[] = deck?.deck_cards ?? [];

  const ownedTypes  = cards.filter((c: DeckCard) => (c.owned_quantity ?? 0) >= c.quantity).length;
  const totalTypes  = cards.length;
  const missingCount = cards.filter((c: DeckCard) => (c.owned_quantity ?? 0) < c.quantity).length;
  const pct = totalTypes > 0 ? Math.round((ownedTypes / totalTypes) * 100) : 0;

  if (isLoading) {
    return (
      <div className="border-t border-gray-200 dark:border-zinc-800 p-4">
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
          {[...Array(10)].map((_, i) => <div key={i} className="skeleton aspect-[3/4] rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 dark:border-zinc-800 p-4">
      {/* Completion bar */}
      {totalTypes > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-zinc-500 mb-1.5">
            <span>{ownedTypes}/{totalTypes} card types owned</span>
            <span className={pct === 100 ? 'text-green-600 font-semibold' : 'text-amber-600 font-medium'}>
              {pct}% complete
            </span>
          </div>
          <div className="h-2 bg-gray-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#10b981' : '#f59e0b' }}
            />
          </div>
        </div>
      )}

      {/* Card grid */}
      {cards.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-500 text-center py-4">No cards added yet. Open the builder to add cards.</p>
      ) : (
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2 mb-4">
          {cards.map((card: DeckCard) => {
            const owned = (card.owned_quantity ?? 0) >= card.quantity;
            return (
              <div
                key={card.id}
                title={`${card.card_name} (${card.owned_quantity ?? 0}/${card.quantity})`}
                className="relative"
              >
                <div className={`aspect-[3/4] rounded-lg overflow-hidden relative ${owned ? '' : 'opacity-40'}`}>
                  <CardImage
                    src={undefined}
                    alt={card.card_name ?? ''}
                    cardName={card.card_name}
                    game={card.card_game}
                    className="w-full h-full"
                  />
                  {!owned && (
                    <div className="absolute inset-0 flex items-end justify-center pb-0.5">
                      <span className="text-white text-xs font-bold drop-shadow-sm">
                        {card.owned_quantity ?? 0}/{card.quantity}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Link
          to={`/collection/decks/${deckId}`}
          className="flex-1 btn-primary py-1.5 text-sm flex items-center justify-center gap-1"
        >
          Open Builder <ChevronRight className="h-3.5 w-3.5" />
        </Link>
        {missingCount > 0 && (
          <Link
            to={`/collection/decks/${deckId}/missing`}
            className="btn-outline py-1.5 px-3 text-sm text-amber-700 dark:text-amber-300 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 dark:bg-amber-900/20 flex items-center gap-1"
          >
            <ShoppingBag className="h-3.5 w-3.5" />
            {missingCount} Missing
          </Link>
        )}
      </div>
    </div>
  );
}

function DeckRow({ deck, onDelete }: { deck: Deck; onDelete: () => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${DECK_GAME_COLOR[deck.game as DeckGame]}`}>
            {DECK_GAME_EMOJI[deck.game as DeckGame]} {deck.game}
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 dark:text-zinc-100 truncate">{deck.name}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500">
              {deck.format ? `${deck.format} · ` : ''}{deck.total_cards} cards
            </p>
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-500 dark:text-zinc-500 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          {deck.is_valid
            ? <ShieldCheck className="h-4 w-4 text-green-500" />
            : <AlertTriangle className="h-4 w-4 text-amber-500" />
          }
          <button
            onClick={() => { if (window.confirm('Delete this deck?')) onDelete(); }}
            className="p-1 text-gray-400 dark:text-zinc-500 hover:text-red-500 transition-colors"
            title="Delete deck"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {expanded && <DeckExpandedContent deckId={deck.id} />}
    </div>
  );
}

// ── New Deck Modal ────────────────────────────────────────────────────────────

type DeckModalStep = 'form' | 'creating' | 'fetching';

function NewDeckModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName]         = useState('');
  const [game, setGame]         = useState<DeckGame>('Pokemon');
  const [format, setFormat]     = useState('');
  const [original, setOriginal] = useState(false);
  const [step, setStep]         = useState<DeckModalStep>('form');
  const { mutate: create }      = useCreateDeck();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('creating');
    create({ name, game, format: format || undefined }, {
      onSuccess: async (deck) => {
        if (!original) {
          setStep('fetching');
          try { await deckService.populateDeck(deck.id, { query: name }); } catch { /* non-fatal */ }
        }
        onCreated(deck.id);
      },
      onError: () => setStep('form'),
    });
  };

  const busy = step !== 'form';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">New Deck</h2>
        {busy ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
            <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">
              {step === 'creating' ? 'Creating deck…' : `Fetching cards for "${name}"…`}
            </p>
            {step === 'fetching' && (
              <p className="text-xs text-gray-500 dark:text-zinc-500">
                Searching {game === 'Magic' ? 'Scryfall' : game === 'Pokemon' ? 'Pokémon TCG' : 'YGOPRODeck'} for a matching deck
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Deck name</label>
              <input
                autoFocus required
                value={name}
                onChange={e => setName(e.target.value)}
                className="input w-full"
                placeholder="e.g. Eldlich, Pikachu ex, Goblin Aggro"
              />
              {!original && name.trim() && (
                <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">We'll search for a matching deck and add cards automatically.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Game</label>
              <select value={game} onChange={e => setGame(e.target.value as DeckGame)} className="input w-full">
                {DECK_GAMES.map(g => <option key={g} value={g}>{DECK_GAME_EMOJI[g]} {g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
                Format <span className="text-gray-500 dark:text-zinc-500 font-normal">(optional)</span>
              </label>
              <input value={format} onChange={e => setFormat(e.target.value)} className="input w-full" placeholder="Standard, Expanded, Legacy…" />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={original}
                onChange={e => setOriginal(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-zinc-700 text-brand-600 dark:text-brand-400 focus:ring-brand-400"
              />
              <span className="text-sm text-gray-600 dark:text-zinc-400">I'm building an original deck <span className="text-gray-500 dark:text-zinc-500">(skip auto-search)</span></span>
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} className="flex-1 btn-outline py-2 text-sm">Cancel</button>
              <button type="submit" className="flex-1 btn-primary py-2 text-sm">
                {original ? 'Create Deck' : 'Create & Fetch Cards'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}



export default function CollectionPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('recently_added');
  const [game, setGame]         = useState('');
  const [setName, setSetName]   = useState('');
  const [condition, setCondition] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage]         = useState(1);
  const [albumsPage, setAlbumsPage] = useState(1);
  const [setsPage, setSetsPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [addModalCard, setAddModalCard] = useState<any | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [quickListItem, setQuickListItem] = useState<any | null>(null);
  const [findListingsCard, setFindListingsCard] = useState<any | null>(null);

  // ── Deck state ──────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const [deckFilterGame, setDeckFilterGame]       = useState<string>('');
  const [showNewDeck, setShowNewDeck]             = useState(false);
  const [showDeckTemplates, setShowDeckTemplates] = useState(false);

  const { data: decks = [], isLoading: decksLoading } = useDecks(
    deckFilterGame ? { game: deckFilterGame } : undefined
  );
  const { data: deckTemplates = [] }                       = useDeckTemplates(
    deckFilterGame ? { game: deckFilterGame } : undefined
  );
  const { mutate: deleteDeck }                             = useDeleteDeck();
  const { mutate: createDeckFromTemplate, isLoading: copyingTemplate } = useCreateDeckFromTemplate();

  // Top 5 swap prompt state
  const [swapCandidate, setSwapCandidate] = useState<any | null>(null);

  const isBulkView = viewMode === 'bulk_merch';

  const commonFilters = {
    search:        search    || undefined,
    sort:          sortBy,
    game:          game      || undefined,
    set_name:      setName   || undefined,
    condition:     condition || undefined,
    status:        statusFilter === 'all' ? undefined : statusFilter,
    item_category: (isBulkView ? 'bulk_merch' : 'single_card') as any,
    limit:         PAGE_SIZE,
  };

  const { data, isLoading } = useCollection({
    ...commonFilters,
    page,
    enabled: viewMode !== 'albums' && viewMode !== 'bulk_merch',
  });

  const { data: albumsData, isLoading: albumsLoading } = useCollection({
    ...commonFilters,
    page: albumsPage,
    enabled: viewMode === 'albums',
  });

  const { data: sets = [], isLoading: setsLoading } = useCollectionSetCompletion(
    game || undefined
  );

  const { data: filters } = useCollectionFilters();

  const { mutate: updateItem } = useUpdateCollectionItem();
  const { mutate: removeItem } = useRemoveFromCollection();
  const { mutate: addToCollection } = useAddToCollection();
  const { mutate: featureItem } = useFeatureCollectionItem();
  const { data: setWatches = [] } = useSetWatches();
  const { mutate: toggleSetWatch } = useToggleSetWatch();
  const { data: featuredItems = [] } = useFeaturedCollection();

  const items           = data?.items ?? [];
  const stats           = data?.stats;
  const totalPages      = data?.pages ?? 1;
  const albumsItems     = albumsData?.items ?? [];
  const albumsTotalPages = albumsData?.pages ?? 1;

  const allSets       = sets as any[];
  const setsTotalPages = Math.max(1, Math.ceil(allSets.length / PAGE_SIZE));
  const pagedSets     = allSets.slice((setsPage - 1) * PAGE_SIZE, setsPage * PAGE_SIZE);

  // Reset section pages when switching view modes
  React.useEffect(() => {
    setPage(1);
    setAlbumsPage(1);
    setSetsPage(1);
  }, [viewMode]);

  const featuredIds = new Set((featuredItems as any[]).map((i: any) => i.id));

  const handleToggleFeature = useCallback((item: any) => {
    const alreadyFeatured = featuredIds.has(item.id);
    if (!alreadyFeatured && (featuredItems as any[]).length >= 5) {
      // Top 5 full — ask user which to swap out
      setSwapCandidate(item);
      return;
    }
    featureItem(
      { id: item.id, featured: !alreadyFeatured },
      { onError: () => toast.error('Failed to update Top 5') }
    );
  }, [featuredIds, featuredItems, featureItem]);

  const handleToggleTrade = useCallback((item: any) => {
    updateItem(
      { id: item.id, data: { is_for_trade: !item.is_for_trade } },
      {
        onSuccess: () => { toast.success(item.is_for_trade ? 'Offer banner removed' : '"Make me an offer" banner added'); },
        onError: () => { toast.error('Failed to update'); },
      }
    );
  }, [updateItem]);

  const handleQuantityUpdate = useCallback((id: string, quantity: number) => {
    updateItem(
      { id, data: { quantity } },
      { onError: () => { toast.error('Failed to update'); } }
    );
  }, [updateItem]);

  const handleRemove = useCallback((id: string) => {
    removeItem(id, { onSuccess: () => { toast.success('Removed from collection'); } });
  }, [removeItem]);

  const resetFilters = () => {
    setGame('');
    setSetName('');
    setCondition('');
    setSearch('');
    setStatusFilter('all');
    setPage(1);
  };

  const hasActiveFilters = !!(game || setName || condition || search);

  // Derived sets list for the set filter dropdown
  const availableSets = game && filters?.sets?.[game] ? filters.sets[game] : [];

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 via-brand-700 to-accent-600 text-white py-10">
        <div className="container-custom">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-display font-bold">My Collection</h1>
              <p className="text-brand-100 text-sm mt-0.5">Track every card you own</p>
            </div>
            <Link
              to="/collection/add"
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-zinc-900/10 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900/20 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Cards
            </Link>
          </div>

          {/* Stats row */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2">
              <div className="bg-white dark:bg-zinc-900/10 backdrop-blur rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-brand-800 dark:text-brand-200" />
                  <p className="text-xs text-brand-800 dark:text-brand-200">Total Cards</p>
                </div>
                <p className="text-2xl font-bold">{stats.total_cards}</p>
              </div>
              <div className="bg-white dark:bg-zinc-900/10 backdrop-blur rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-brand-800 dark:text-brand-200" />
                  <p className="text-xs text-brand-800 dark:text-brand-200">Unique Cards</p>
                </div>
                <p className="text-2xl font-bold">{stats.unique_cards}</p>
              </div>
              <div className="bg-white dark:bg-zinc-900/10 backdrop-blur rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-brand-800 dark:text-brand-200" />
                  <p className="text-xs text-brand-800 dark:text-brand-200">Est. Value</p>
                </div>
                <p className="text-2xl font-bold">{formatPrice(stats.total_value)}</p>
              </div>
              <div className="bg-white dark:bg-zinc-900/10 backdrop-blur rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-brand-800 dark:text-brand-200" />
                  <p className="text-xs text-brand-800 dark:text-brand-200">Total Sold</p>
                </div>
                <p className="text-2xl font-bold">{formatPrice(stats.total_sold)}</p>
              </div>
              <div className="bg-white dark:bg-zinc-900/10 backdrop-blur rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingCart className="h-4 w-4 text-brand-800 dark:text-brand-200" />
                  <p className="text-xs text-brand-800 dark:text-brand-200">Paid For</p>
                </div>
                <p className="text-2xl font-bold">{formatPrice(stats.total_paid)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="container-custom py-6">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 items-center mb-4">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
            <input
              type="text"
              placeholder="Search your collection..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none border border-gray-200 dark:border-zinc-800 rounded-xl px-3 py-2.5 pr-8 text-sm bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 dark:text-zinc-500 pointer-events-none" />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
              hasActiveFilters
                ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-300 text-brand-700 dark:text-brand-300'
                : 'bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 text-gray-700 dark:text-zinc-300 hover:border-brand-300'
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-brand-600 text-white rounded-full h-4 w-4 text-xs flex items-center justify-center">
                {[game, setName, condition].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* View mode */}
          <div className="flex border border-gray-200 dark:border-zinc-800 rounded-xl overflow-hidden bg-white dark:bg-zinc-900">
            {([
              { mode: 'grid',       icon: <LayoutGrid className="h-4 w-4" />,   label: 'Grid' },
              { mode: 'list',       icon: <List className="h-4 w-4" />,         label: 'List' },
              { mode: 'albums',     icon: <BookOpen className="h-4 w-4" />,     label: 'Albums' },
              { mode: 'sets',       icon: <Package className="h-4 w-4" />,      label: 'Sets' },
              { mode: 'offers',     icon: <Handshake className="h-4 w-4" />,    label: 'Offers' },
              { mode: 'decks',      icon: <Layers className="h-4 w-4" />,       label: 'Decks' },
              { mode: 'bulk_merch', icon: <ShoppingBag className="h-4 w-4" />, label: 'Bulk & Merch' },
            ] as { mode: ViewMode; icon: React.ReactNode; label: string }[]).map(({ mode, icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-2.5 text-sm font-medium transition-colors ${
                  viewMode === mode ? 'bg-brand-600 text-white' : 'text-gray-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900'
                }`}
                title={label}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-zinc-800">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setStatusFilter(tab.value); setPage(1); }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors -mb-px ${
                statusFilter === tab.value
                  ? 'text-brand-600 dark:text-brand-400 border-brand-600 bg-white dark:bg-zinc-900'
                  : 'text-gray-500 dark:text-zinc-500 border-transparent hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Expandable filters */}
        {showFilters && (
          <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-xl p-4 mb-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {/* Game filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-zinc-500 mb-1.5 uppercase tracking-wide">Game / Sport</label>
              <select
                value={game}
                onChange={(e) => { setGame(e.target.value); setSetName(''); setPage(1); }}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-2.5 py-2 text-sm bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                <option value="">All games</option>
                {(filters?.games ?? []).map((g: string) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            {/* Set filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-zinc-500 mb-1.5 uppercase tracking-wide">Set</label>
              <select
                value={setName}
                onChange={(e) => { setSetName(e.target.value); setPage(1); }}
                disabled={!game}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-2.5 py-2 text-sm bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
              >
                <option value="">All sets</option>
                {availableSets.map((s: string) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Condition filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-zinc-500 mb-1.5 uppercase tracking-wide">Condition</label>
              <select
                value={condition}
                onChange={(e) => { setCondition(e.target.value); setPage(1); }}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-2.5 py-2 text-sm bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                <option value="">Any condition</option>
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Reset */}
            {hasActiveFilters && (
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="w-full py-2 text-sm text-red-500 hover:text-red-700 dark:text-red-300 font-medium border border-red-200 hover:border-red-300 rounded-lg transition-colors"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── SETS VIEW ── */}
        {viewMode === 'sets' && (
          <div>
            {setsLoading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton h-16 rounded-xl" />
                ))}
              </div>
            ) : allSets.length === 0 ? (
              <div className="text-center py-20">
                <Package className="h-16 w-16 mx-auto text-gray-200 mb-4" />
                <h3 className="text-lg font-bold text-gray-700 dark:text-zinc-300 mb-2">No sets yet</h3>
                <p className="text-gray-500 dark:text-zinc-500 text-sm mb-4">Add cards to your collection to see set completion</p>
                <Link to="/explore" className="btn-primary text-sm">Explore Cards</Link>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pagedSets.map((s: any) => (
                    <SetCard
                      key={`${s.game}-${s.set_name}`}
                      s={s}
                      isWatching={(setWatches as any[]).some(
                        (w: any) => w.game === s.game && w.set_name === s.set_name
                      )}
                      onToggleWatch={() => toggleSetWatch({ game: s.game, set_name: s.set_name })}
                    />
                  ))}
                </div>
                {setsTotalPages > 1 && (
                  <Pagination page={setsPage} totalPages={setsTotalPages} onPageChange={setSetsPage} />
                )}
              </>
            )}
          </div>
        )}

        {/* ── GRID VIEW ── */}
        {viewMode === 'grid' && (
          <>
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[...Array(18)].map((_, i) => <div key={i} className="skeleton h-72 rounded-xl" />)}
              </div>
            ) : items.length === 0 ? (
              <EmptyState hasFilters={hasActiveFilters} onReset={resetFilters} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {items.map((item: any) => {
                  const isActiveListing = item.listing_status === 'active';
                  const isSold = !!item.last_sold_at;
                  const isFeatured = featuredIds.has(item.id);
                  return (
                  <div key={item.id} className="group relative">
                    <Link
                      to={`/cards/${item.card_id}`}
                      className="block bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-zinc-800 hover:shadow-md hover:border-brand-200 transition-all"
                    >
                      <div className="aspect-[3/4] bg-gradient-to-br from-zinc-800 to-gray-200 relative overflow-hidden">
                        <CardImage
                          src={item.listing_image_urls?.[0]}
                          alt={item.card?.name}
                          cardName={item.card?.name}
                          cardNumber={item.card?.card_number}
                          game={item.card?.game}
                          className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                        />
                        {/* Quantity badge */}
                        {item.quantity > 1 && (
                          <div className="absolute top-1.5 right-1.5 bg-brand-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow">
                            ×{item.quantity}
                          </div>
                        )}
                        {/* Status badges */}
                        <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
                          {isActiveListing && (
                            <span className="bg-emerald-600 text-white text-xs rounded-full px-1.5 py-0.5 font-medium leading-tight">
                              Listed
                            </span>
                          )}
                          {isSold && !isActiveListing && (
                            <span className="bg-white dark:bg-zinc-900 text-white text-xs rounded-full px-1.5 py-0.5 font-medium leading-tight">
                              Sold
                            </span>
                          )}
                        </div>
                        {/* "Make me an offer" banner */}
                        {item.is_for_trade && (
                          <div className="absolute bottom-0 inset-x-0 bg-violet-600/90 backdrop-blur-sm text-white text-xs font-semibold text-center py-1 flex items-center justify-center gap-1">
                            <Handshake className="h-3 w-3" /> Make me an offer
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="font-semibold text-xs truncate text-gray-900 dark:text-zinc-100">{item.card?.name}</p>
                        <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{item.card?.set_name}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500 dark:text-zinc-500">{item.condition}</span>
                          {isActiveListing && item.listing_price ? (
                            <span className="text-xs font-bold text-emerald-600">
                              ${item.listing_price.toFixed(2)}
                            </span>
                          ) : item.card?.market_price ? (
                            <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                              {formatPrice(item.card.market_price)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </Link>

                    {/* Hover controls */}
                    <div className="absolute inset-x-2 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                      {/* Sell / Buy More buttons */}
                      <div className="flex gap-1">
                        {!isActiveListing && (
                          <button
                            onClick={(e) => { e.preventDefault(); setQuickListItem(item); }}
                            className="flex-1 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow"
                          >
                            Sell
                          </button>
                        )}
                        {isActiveListing && item.listing_id && (
                          <Link
                            to={`/listings/${item.listing_id}`}
                            className="flex-1 py-1 rounded-lg text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Listing
                          </Link>
                        )}
                        <button
                          onClick={(e) => { e.preventDefault(); setFindListingsCard(item.card); }}
                          className="px-2 py-1 rounded-lg text-xs font-semibold bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors shadow"
                          title="Find listings from other sellers"
                        >
                          <ShoppingCart className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* Quantity + delete + star row */}
                      <div className="flex items-center bg-white dark:bg-zinc-900/90 rounded-lg shadow px-1">
                        <QuantityControl
                          item={item}
                          onUpdate={handleQuantityUpdate}
                          onRemove={handleRemove}
                        />
                        <button
                          onClick={(e) => { e.preventDefault(); handleToggleFeature(item); }}
                          className={`p-1 transition-colors ${isFeatured ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-400 dark:text-zinc-500 hover:text-yellow-400'}`}
                          title={isFeatured ? 'Remove from Top 5' : 'Add to Top 5'}
                        >
                          <Star className={`h-3.5 w-3.5 ${isFeatured ? 'fill-yellow-400' : ''}`} />
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); handleToggleTrade(item); }}
                          className={`p-1 transition-colors ${item.is_for_trade ? 'text-violet-500 hover:text-violet-600' : 'text-gray-400 dark:text-zinc-500 hover:text-violet-400'}`}
                          title={item.is_for_trade ? 'Remove "Make me an offer" banner' : 'Show "Make me an offer" banner'}
                        >
                          <Handshake className={`h-3.5 w-3.5 ${item.is_for_trade ? 'fill-violet-100' : ''}`} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            if (window.confirm(`Remove ${item.card?.name}?`)) {
                              handleRemove(item.id);
                            }
                          }}
                          className="ml-auto p-1 text-red-400 hover:text-red-600 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── LIST VIEW ── */}
        {viewMode === 'list' && (
          <>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(10)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
              </div>
            ) : items.length === 0 ? (
              <EmptyState hasFilters={hasActiveFilters} onReset={resetFilters} />
            ) : (
              <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-x-auto">
                {/* Column headers */}
                <div className="grid grid-cols-[3rem_1fr_1fr_6rem_5rem_6rem_3rem] gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-zinc-800 text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wide">
                  <span></span>
                  <span>Card</span>
                  <span className="hidden sm:block">Set</span>
                  <span>Condition</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Value</span>
                  <span></span>
                </div>

                {items.map((item: any, idx: number) => (
                  <div
                    key={item.id}
                    className={`grid grid-cols-[3rem_1fr_1fr_6rem_5rem_6rem_3rem] gap-3 px-4 py-3 items-center hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors ${
                      idx < items.length - 1 ? 'border-b border-gray-50' : ''
                    }`}
                  >
                    {/* Thumbnail */}
                    <Link to={`/cards/${item.card_id}`} className="block h-10 w-7 rounded overflow-hidden bg-gray-100 dark:bg-zinc-800 flex-shrink-0">
                      <CardImage src={item.listing_image_urls?.[0]} alt={item.card?.name} cardName={item.card?.name} cardNumber={item.card?.card_number} game={item.card?.game} className="w-full h-full" compact />
                    </Link>

                    {/* Name */}
                    <div className="min-w-0">
                      <Link to={`/cards/${item.card_id}`} className="font-semibold text-sm text-gray-900 dark:text-zinc-100 hover:text-brand-600 dark:text-brand-400 truncate block">
                        {item.card?.name}
                      </Link>
                      {item.notes && <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{item.notes}</p>}
                    </div>

                    {/* Set */}
                    <p className="text-xs text-gray-500 dark:text-zinc-500 truncate hidden sm:block">
                      {item.card?.game && <span className="text-gray-500 dark:text-zinc-500">{item.card.game} · </span>}
                      {item.card?.set_name}
                    </p>

                    {/* Condition */}
                    <span className="text-xs font-medium text-gray-600 dark:text-zinc-400">{item.condition}</span>

                    {/* Quantity controls */}
                    <div className="flex justify-center">
                      <QuantityControl item={item} onUpdate={handleQuantityUpdate} onRemove={handleRemove} />
                    </div>

                    {/* Value */}
                    <div className="text-right">
                      {item.card?.market_price ? (
                        <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{formatPrice(item.card.market_price)}</span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 justify-end">
                      {item.listing_status === 'active' ? (
                        <Link
                          to={`/listings/${item.listing_id}`}
                          className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:bg-emerald-900/40 transition-colors"
                        >
                          Listed
                        </Link>
                      ) : (
                        <button
                          onClick={() => setQuickListItem(item)}
                          className="p-1 text-gray-500 dark:text-zinc-500 hover:text-emerald-600 transition-colors"
                          title="Sell this card"
                        >
                          <Tag className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setFindListingsCard(item.card)}
                        className="p-1 text-gray-400 dark:text-zinc-500 hover:text-brand-500 transition-colors"
                        title="Find listings from other sellers"
                      >
                        <ShoppingCart className="h-3.5 w-3.5" />
                      </button>
                      {item.is_for_trade && (
                        <ArrowLeftRight className="h-3.5 w-3.5 text-purple-500" aria-label="For trade" />
                      )}
                      <button
                        onClick={() => {
                          if (window.confirm(`Remove ${item.card?.name}?`)) handleRemove(item.id);
                        }}
                        className="p-1 text-gray-400 dark:text-zinc-500 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── ALBUMS VIEW ── */}
        {viewMode === 'albums' && (
          <>
            {albumsLoading ? (
              <div className="space-y-8">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <div className="skeleton h-8 w-48 rounded-lg" />
                    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
                      {[...Array(8)].map((_, j) => <div key={j} className="skeleton aspect-[3/4] rounded-xl" />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : albumsItems.length === 0 ? (
              <EmptyState hasFilters={hasActiveFilters} onReset={resetFilters} />
            ) : (
              <>
                <AlbumsView
                  items={albumsItems}
                  featured={featuredItems as any[]}
                  onSell={setQuickListItem}
                  onBuyMore={setFindListingsCard}
                  onRemove={handleRemove}
                  onQuantityUpdate={handleQuantityUpdate}
                  onToggleFeature={handleToggleFeature}
                  onToggleTrade={handleToggleTrade}
                />
                {albumsTotalPages > 1 && (
                  <Pagination page={albumsPage} totalPages={albumsTotalPages} onPageChange={setAlbumsPage} />
                )}
              </>
            )}
          </>
        )}

        {/* ── OFFERS VIEW ── */}
        {viewMode === 'offers' && (
          <>
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[...Array(12)].map((_, i) => <div key={i} className="skeleton aspect-[3/4] rounded-xl" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-24">
                <Handshake className="h-12 w-12 text-gray-400 dark:text-zinc-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-zinc-300 mb-1">No cards marked for offers</h3>
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  Hover any card in your collection and click the <Handshake className="h-3.5 w-3.5 inline" /> icon to show a "Make me an offer" banner.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-violet-500" />
                  <p className="text-sm text-gray-500 dark:text-zinc-500">
                    {items.length} card{items.length !== 1 ? 's' : ''} open to offers or trades
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {items.map((item: any) => {
                    const isActiveListing = item.listing_status === 'active';
                    const isFeatured = featuredIds.has(item.id);
                    return (
                      <div key={item.id} className="group relative">
                        <Link
                          to={`/cards/${item.card_id}`}
                          className="block bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-violet-100 hover:shadow-md hover:border-violet-300 transition-all"
                        >
                          <div className="aspect-[3/4] bg-gradient-to-br from-violet-900/40 to-zinc-800 relative overflow-hidden">
                            <CardImage
                              src={item.listing_image_urls?.[0]}
                              alt={item.card?.name}
                              cardName={item.card?.name}
                              cardNumber={item.card?.card_number}
                              game={item.card?.game}
                              className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                            />
                            {item.quantity > 1 && (
                              <div className="absolute top-1.5 right-1.5 bg-brand-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow">
                                ×{item.quantity}
                              </div>
                            )}
                            <div className="absolute bottom-0 inset-x-0 bg-violet-600/90 backdrop-blur-sm text-white text-xs font-semibold text-center py-1 flex items-center justify-center gap-1">
                              <Handshake className="h-3 w-3" /> Make me an offer
                            </div>
                          </div>
                          <div className="p-2.5">
                            <p className="font-semibold text-xs truncate text-gray-900 dark:text-zinc-100">{item.card?.name}</p>
                            <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{item.card?.set_name}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500 dark:text-zinc-500">{item.condition}</span>
                              {item.card?.market_price ? (
                                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">{formatPrice(item.card.market_price)}</span>
                              ) : null}
                            </div>
                          </div>
                        </Link>

                        {/* Hover controls */}
                        <div className="absolute inset-x-2 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                          <div className="flex gap-1">
                            {!isActiveListing && (
                              <button
                                onClick={(e) => { e.preventDefault(); setQuickListItem(item); }}
                                className="flex-1 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow"
                              >
                                Sell
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.preventDefault(); setFindListingsCard(item.card); }}
                              className="px-2 py-1 rounded-lg text-xs font-semibold bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors shadow"
                            >
                              <ShoppingCart className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="flex items-center bg-white dark:bg-zinc-900/90 rounded-lg shadow px-1">
                            <QuantityControl item={item} onUpdate={handleQuantityUpdate} onRemove={handleRemove} />
                            <button
                              onClick={(e) => { e.preventDefault(); handleToggleFeature(item); }}
                              className={`p-1 transition-colors ${isFeatured ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-400 dark:text-zinc-500 hover:text-yellow-400'}`}
                              title={isFeatured ? 'Remove from Top 5' : 'Add to Top 5'}
                            >
                              <Star className={`h-3.5 w-3.5 ${isFeatured ? 'fill-yellow-400' : ''}`} />
                            </button>
                            <button
                              onClick={(e) => { e.preventDefault(); handleToggleTrade(item); }}
                              className="p-1 text-violet-500 hover:text-violet-600 transition-colors"
                              title='Remove "Make me an offer" banner'
                            >
                              <Handshake className="h-3.5 w-3.5 fill-violet-100" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                if (window.confirm(`Remove ${item.card?.name}?`)) handleRemove(item.id);
                              }}
                              className="ml-auto p-1 text-red-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ── DECKS VIEW ── */}
        {viewMode === 'decks' && (
          <div>
            {/* Deck toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="flex gap-2 flex-wrap flex-1">
                <button
                  onClick={() => setDeckFilterGame('')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!deckFilterGame ? 'bg-brand-600 text-white' : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:border-brand-300'}`}
                >
                  All Games
                </button>
                {DECK_GAMES.map(g => (
                  <button
                    key={g}
                    onClick={() => setDeckFilterGame(deckFilterGame === g ? '' : g)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${deckFilterGame === g ? 'bg-brand-600 text-white' : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:border-brand-300'}`}
                  >
                    {DECK_GAME_EMOJI[g]} {g}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowNewDeck(true)}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                <Plus className="h-4 w-4" /> New Deck
              </button>
            </div>

            {/* Deck list (expandable rows, like Sets view) */}
            {decksLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}
              </div>
            ) : (decks as Deck[]).length === 0 ? (
              <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-gray-200 dark:border-zinc-800 p-10 text-center mb-4">
                <Layers className="h-10 w-10 text-gray-400 dark:text-zinc-500 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-zinc-500 mb-4">No decks yet — create one above</p>
              </div>
            ) : (
              <div className="space-y-2 mb-6">
                {(decks as Deck[]).map((deck) => (
                  <DeckRow key={deck.id} deck={deck} onDelete={() => deleteDeck(deck.id)} />
                ))}
              </div>
            )}

            {/* Meta templates */}
            <div>
              <button
                onClick={() => setShowDeckTemplates(v => !v)}
                className="flex items-center gap-2 text-brand-600 dark:text-brand-400 font-medium text-sm mb-4 hover:underline"
              >
                <BookOpen className="h-4 w-4" />
                {showDeckTemplates ? 'Hide' : 'Browse'} meta deck templates
              </button>
              {showDeckTemplates && (
                (deckTemplates as any[]).length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-zinc-500">No templates available yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(deckTemplates as any[]).map((t: any) => (
                      <div key={t.id} className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${DECK_GAME_COLOR[t.game as DeckGame] ?? 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400'}`}>
                            {t.game}
                          </span>
                          {t.tier && (
                            <span className="text-xs font-bold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">Tier {t.tier}</span>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 dark:text-zinc-100 mb-1">{t.name}</h3>
                        {t.archetype && <p className="text-xs text-gray-500 dark:text-zinc-500 mb-2">{t.archetype}</p>}
                        <div className="text-xs text-gray-500 dark:text-zinc-500 mb-3">{t.times_copied} copies made</div>
                        <button
                          disabled={copyingTemplate}
                          onClick={() => createDeckFromTemplate({ templateId: t.id }, {
                            onSuccess: (deck: any) => navigate(`/collection/decks/${deck.id}`),
                          })}
                          className="w-full btn-outline py-1.5 text-sm"
                        >
                          Use this template
                        </button>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Bulk & Merch view */}
        {viewMode === 'bulk_merch' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500 dark:text-zinc-500">Booster packs, boxes, plushies, and other collectibles</p>
              <button
                onClick={() => setShowBulkModal(true)}
                className="btn-primary flex items-center gap-2 py-2 px-4 text-sm"
              >
                <Plus className="h-4 w-4" /> Add Item
              </button>
            </div>
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-40 rounded-xl" />)}
              </div>
            ) : (data?.items ?? []).length === 0 ? (
              <div className="text-center py-20">
                <ShoppingBag className="h-12 w-12 mx-auto text-gray-300 dark:text-zinc-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-zinc-300 mb-1">No bulk items yet</h3>
                <p className="text-sm text-gray-500 dark:text-zinc-500 mb-4">Track your booster packs, boxes, plushies, and more.</p>
                <button onClick={() => setShowBulkModal(true)} className="btn-primary py-2 px-5 text-sm">
                  Add Your First Item
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {(data?.items ?? []).map((item: any) => (
                  <div key={item.id} className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5 text-brand-500 shrink-0" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                        {item.item_category?.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="font-semibold text-sm text-gray-900 dark:text-zinc-100 leading-tight">{item.item_name}</p>
                    {item.item_brand && <p className="text-xs text-gray-500 dark:text-zinc-400">{item.item_brand}</p>}
                    <div className="mt-auto flex items-center justify-between text-xs text-gray-500 dark:text-zinc-400">
                      <span>{item.condition}</span>
                      <span>x{item.quantity}</span>
                    </div>
                    {item.acquisition_price && (
                      <p className="text-xs text-brand-600 dark:text-brand-400 font-medium">Paid {formatPrice(item.acquisition_price)}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pagination (grid, list, offers) */}
        {['grid', 'list', 'offers'].includes(viewMode) && totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>

      {/* Add Bulk/Merch modal */}
      {showBulkModal && (
        <AddBulkMerchModal
          onConfirm={(data) => {
            addToCollection(data as any);
            setShowBulkModal(false);
          }}
          onCancel={() => setShowBulkModal(false)}
        />
      )}

      {/* Add to Collection modal */}
      {addModalCard && (
        <AddToCollectionModal
          card={addModalCard}
          onClose={() => setAddModalCard(null)}
        />
      )}

      {/* Quick List modal */}
      {quickListItem && (
        <QuickListingModal
          item={quickListItem}
          onClose={() => setQuickListItem(null)}
        />
      )}

      {/* Find Listings modal */}
      {findListingsCard && (
        <FindListingsModal
          card={findListingsCard}
          onClose={() => setFindListingsCard(null)}
        />
      )}

      {/* New deck modal */}
      {showNewDeck && (
        <NewDeckModal
          onClose={() => setShowNewDeck(false)}
          onCreated={(id) => { setShowNewDeck(false); navigate(`/collection/decks/${id}`); }}
        />
      )}

      {/* Top 5 full — swap prompt */}
      {swapCandidate && (
        <Top5SwapModal
          candidate={swapCandidate}
          featured={featuredItems as any[]}
          onSwap={(removeId) => {
            featureItem({ id: removeId, featured: false });
            featureItem({ id: swapCandidate.id, featured: true });
            setSwapCandidate(null);
          }}
          onClose={() => setSwapCandidate(null)}
        />
      )}
    </div>
  );
}

// ── Top 5 swap modal ────────────────────────────────────────────────────────

function Top5SwapModal({
  candidate,
  featured,
  onSwap,
  onClose,
}: {
  candidate: any;
  featured: any[];
  onSwap: (removeId: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-zinc-800">
          <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">Top 5 is Full</h2>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mt-0.5">
            Pick a card to replace with <span className="font-semibold text-gray-800 dark:text-zinc-200">{candidate.card?.name}</span>:
          </p>
        </div>
        <div className="p-4 space-y-2">
          {featured.map((item: any) => (
            <button
              key={item.id}
              onClick={() => onSwap(item.id)}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-zinc-800 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 transition-colors text-left"
            >
              <div className="h-12 w-9 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 flex-shrink-0">
                <CardImage src={item.listing_image_urls?.[0]} alt={item.card?.name} cardName={item.card?.name} cardNumber={item.card?.card_number} game={item.card?.game} className="w-full h-full" compact />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{item.card?.name}</p>
                <p className="text-xs text-gray-500 dark:text-zinc-500">{item.condition}</p>
              </div>
              <span className="ml-auto text-xs text-red-500 font-medium flex-shrink-0">Remove</span>
            </button>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-zinc-800">
          <button onClick={onClose} className="w-full py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Albums view ─────────────────────────────────────────────────────────────

function AlbumsView({
  items,
  featured,
  onSell,
  onBuyMore,
  onRemove,
  onQuantityUpdate,
  onToggleFeature,
  onToggleTrade,
}: {
  items: any[];
  featured: any[];
  onSell: (item: any) => void;
  onBuyMore: (card: any) => void;
  onRemove: (id: string) => void;
  onQuantityUpdate: (id: string, qty: number) => void;
  onToggleFeature: (item: any) => void;
  onToggleTrade: (item: any) => void;
}) {
  const featuredIds = new Set(featured.map((i) => i.id));

  // Group by game, "Unknown" / null goes last
  const grouped = items.reduce<Record<string, any[]>>((acc, item) => {
    const g = item.card?.game || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  const games = Object.keys(grouped).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return grouped[b].length - grouped[a].length; // most cards first
  });

  return (
    <div className="space-y-4">
      {/* ── Top 5 pinned album ── */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-yellow-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Top 5</h2>
            <span className="text-sm text-gray-500 dark:text-zinc-500 font-normal">{featured.length}/5</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-zinc-500">Shown on your dashboard — star cards to add them</p>
        </div>
        {featured.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-500 italic py-2">
            No cards pinned yet. Click the ★ on any card to feature it here.
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {featured.map((item: any) => (
              <div key={item.id} className="group relative">
                <Link to={`/cards/${item.card_id}`} className="block bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-yellow-100 hover:shadow-md transition-all">
                  <div className="aspect-[3/4] bg-gradient-to-br from-yellow-900/40 to-zinc-800 relative overflow-hidden">
                    <CardImage src={item.listing_image_urls?.[0]} alt={item.card?.name} cardName={item.card?.name} cardNumber={item.card?.card_number} game={item.card?.game} className="w-full h-full" />
                  </div>
                  <div className="p-1.5">
                    <p className="font-semibold text-xs truncate text-gray-900 dark:text-zinc-100 leading-tight">{item.card?.name}</p>
                    <p className="text-xs text-gray-500 dark:text-zinc-500 truncate leading-tight">{item.condition}</p>
                  </div>
                </Link>
                <button
                  onClick={() => onToggleFeature(item)}
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white dark:bg-zinc-900/80 rounded-full p-0.5"
                  title="Remove from Top 5"
                >
                  <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Per-game albums ── */}
      {games.map((game) => (
        <GameAlbum
          key={game}
          game={game}
          items={grouped[game]}
          featuredIds={featuredIds}
          onToggleFeature={onToggleFeature}
          onToggleTrade={onToggleTrade}
          onSell={onSell}
          onBuyMore={onBuyMore}
          onRemove={onRemove}
          onQuantityUpdate={onQuantityUpdate}
        />
      ))}
    </div>
  );
}

type AlbumFilter = 'all' | 'listed' | 'not_listed';

function GameAlbum({
  game,
  items,
  featuredIds,
  onToggleFeature,
  onToggleTrade,
  onSell,
  onBuyMore,
  onRemove,
  onQuantityUpdate,
}: {
  game: string;
  items: any[];
  featuredIds: Set<string>;
  onToggleFeature: (item: any) => void;
  onToggleTrade: (item: any) => void;
  onSell: (item: any) => void;
  onBuyMore: (card: any) => void;
  onRemove: (id: string) => void;
  onQuantityUpdate: (id: string, qty: number) => void;
}) {
  const [filter, setFilter] = useState<AlbumFilter>('all');
  const [collapsed, setCollapsed] = useState(false);

  const emoji = GAME_EMOJI[game] ?? '🃏';
  const listedCount    = items.filter((i) => i.listing_status === 'active').length;
  const notListedCount = items.length - listedCount;

  const visible = items.filter((item) => {
    if (filter === 'listed')     return item.listing_status === 'active';
    if (filter === 'not_listed') return item.listing_status !== 'active';
    return true;
  });

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
      {/* Album header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 group"
        >
          <span className="text-2xl">{emoji}</span>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100 group-hover:text-brand-600 dark:text-brand-400 transition-colors">
            {game}
          </h2>
          <span className="text-sm text-gray-500 dark:text-zinc-500 font-normal">
            {items.length} card{items.length !== 1 ? 's' : ''}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 dark:text-zinc-500 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          />
        </button>

        {/* Listed / Not Listed toggle */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-zinc-800 rounded-lg p-0.5 text-xs">
          {([
            { value: 'all',        label: `All (${items.length})` },
            { value: 'listed',     label: `Listed (${listedCount})` },
            { value: 'not_listed', label: `Not Listed (${notListedCount})` },
          ] as { value: AlbumFilter; label: string }[]).map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setFilter(value)}
              className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                filter === value
                  ? 'bg-white dark:bg-zinc-900 shadow text-gray-900 dark:text-zinc-100'
                  : 'text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Cards grid */}
      {!collapsed && (
        visible.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-500 italic py-4">
            No {filter === 'listed' ? 'listed' : 'unlisted'} cards in this album.
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {visible.map((item) => {
              const isActiveListing = item.listing_status === 'active';
              const isFeatured = featuredIds.has(item.id);
              return (
                <div key={item.id} className="group relative">
                  <Link
                    to={`/cards/${item.card_id}`}
                    className="block bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-zinc-800 hover:shadow-md hover:border-brand-200 transition-all"
                  >
                    <div className="aspect-[3/4] bg-gradient-to-br from-zinc-800 to-gray-200 relative overflow-hidden">
                      <CardImage
                        src={item.listing_image_urls?.[0]}
                        alt={item.card?.name}
                        cardName={item.card?.name}
                        cardNumber={item.card?.card_number}
                        game={item.card?.game}
                        className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                      />
                      {item.quantity > 1 && (
                        <div className="absolute top-1 right-1 bg-brand-600 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center shadow">
                          {item.quantity}
                        </div>
                      )}
                      {isActiveListing && (
                        <div className="absolute top-1 left-1">
                          <span className="bg-emerald-600 text-white text-xs rounded-full px-1 py-0.5 font-medium leading-tight">
                            Listed
                          </span>
                        </div>
                      )}
                      {item.is_for_trade && (
                        <div className="absolute bottom-0 inset-x-0 bg-violet-600/90 text-white text-xs font-semibold text-center py-0.5 flex items-center justify-center gap-0.5">
                          <Handshake className="h-2.5 w-2.5" /> Offer
                        </div>
                      )}
                    </div>
                    <div className="p-1.5">
                      <p className="font-semibold text-xs truncate text-gray-900 dark:text-zinc-100 leading-tight">{item.card?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-500 truncate leading-tight">{item.condition}</p>
                    </div>
                  </Link>

                  {/* Hover actions */}
                  <div className="absolute inset-x-1 bottom-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    {!isActiveListing ? (
                      <button
                        onClick={(e) => { e.preventDefault(); onSell(item); }}
                        className="flex-1 py-0.5 rounded text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow"
                      >
                        Sell
                      </button>
                    ) : item.listing_id ? (
                      <Link
                        to={`/listings/${item.listing_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 py-0.5 rounded text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 shadow text-center"
                      >
                        View
                      </Link>
                    ) : null}
                    <button
                      onClick={(e) => { e.preventDefault(); onBuyMore(item.card); }}
                      className="px-1.5 py-0.5 rounded text-xs bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 shadow"
                      title="Buy more from other sellers"
                    >
                      <ShoppingCart className="h-3 w-3" />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); onToggleFeature(item); }}
                      className={`px-1.5 py-0.5 rounded text-xs shadow transition-colors ${
                        isFeatured
                          ? 'bg-yellow-400 text-white hover:bg-yellow-500'
                          : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-500 dark:text-zinc-500 hover:text-yellow-400 hover:border-yellow-300'
                      }`}
                      title={isFeatured ? 'Remove from Top 5' : 'Add to Top 5'}
                    >
                      <Star className={`h-3 w-3 ${isFeatured ? 'fill-white' : ''}`} />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); onToggleTrade(item); }}
                      className={`px-1.5 py-0.5 rounded text-xs shadow transition-colors ${
                        item.is_for_trade
                          ? 'bg-violet-500 text-white hover:bg-violet-600'
                          : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-500 dark:text-zinc-500 hover:text-violet-500 hover:border-violet-300'
                      }`}
                      title={item.is_for_trade ? 'Remove offer banner' : 'Show "Make me an offer" banner'}
                    >
                      <Handshake className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      <div className="mt-6 border-b border-gray-200 dark:border-zinc-800" />
    </div>
  );
}

// ── Set card with link to detail page ────────────────────────────────────────

function SetCard({
  s,
  isWatching,
  onToggleWatch,
}: {
  s: any;
  isWatching: boolean;
  onToggleWatch: () => void;
}) {
  const isComplete = s.completion_pct >= 100;
  const detailUrl = `/collection/sets/detail?game=${encodeURIComponent(s.game)}&set_name=${encodeURIComponent(s.set_name)}`;

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 dark:text-zinc-500 mb-0.5">{s.game}</p>
            <Link
              to={detailUrl}
              className="font-semibold text-gray-900 dark:text-zinc-100 hover:text-brand-600 dark:text-brand-400 hover:underline truncate block"
            >
              {s.set_name}
            </Link>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isComplete && <span className="text-lg" title="Complete!">🏆</span>}
            {!isComplete && (
              <button
                onClick={onToggleWatch}
                title={isWatching ? 'Stop watching this set' : 'Notify me when missing cards are listed'}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                  isWatching
                    ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 hover:text-red-500'
                    : 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 hover:text-brand-600 dark:text-brand-400'
                }`}
              >
                {isWatching ? <><BellOff className="h-3 w-3" /> Watching</> : <><Bell className="h-3 w-3" /> Watch</>}
              </button>
            )}
          </div>
        </div>

        <SetCompletionBar
          owned={s.owned}
          total={s.total}
          completionPct={s.completion_pct}
          setName={s.set_name}
          showLabel={true}
          size="md"
        />

        <div className="mt-3">
          <Link
            to={detailUrl}
            className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-medium"
          >
            View all {s.total} cards →
          </Link>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ hasFilters, onReset }: { hasFilters: boolean; onReset: () => void }) {
  return (
    <div className="text-center py-24">
      <p className="text-6xl mb-4">📦</p>
      {hasFilters ? (
        <>
          <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">No cards match your filters</h3>
          <button onClick={onReset} className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline">
            Clear filters
          </button>
        </>
      ) : (
        <>
          <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">Your collection is empty</h3>
          <p className="text-gray-500 dark:text-zinc-500 mb-6 text-sm">Browse cards and add them to your collection</p>
          <Link to="/explore" className="btn-primary">Explore Cards</Link>
        </>
      )}
    </div>
  );
}
